import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, Plus, Calendar, X } from "lucide-react";
import { format } from "date-fns";
import { useTopBar } from "../../../../context/TopBarContext";
import { usePlaylist } from "../../../../context/PlaylistContext";
import { ReactComponent as All } from "../../../../assets/icons/svg/all.svg";
import { ReactComponent as Landscape } from "../../../../assets/icons/svg/landscape.svg";
import { ReactComponent as Portrait } from "../../../../assets/icons/svg/Protrait.svg";
import { ReactComponent as ScheduledCreative } from "../../../../assets/icons/svg/Scheduled-Creative.svg";
import ScheduledCreativesCard from "../../../ui/Scheduled_Creatives_Card";
import PlaylistCard from "../../../ui/Playlist_Card";
import { useScheduledCreatives } from "../../../../context/ScheduledCreativesContext";
import { ReactComponent as Icon } from "../../../../assets/icons/svg/Schedule-creative-icon.svg";
import { useCreatives } from "../../../../context/CreativeContext";

// CustomCalendar Component
interface CustomCalendarProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  minDate?: Date;
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  selectedDate,
  onDateChange,
  minDate,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const startOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  );
  const endOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  );
  const startDay = startOfMonth.getDay();
  const daysInMonth = endOfMonth.getDate();

  const prevMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  const nextMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );

  const renderDays = () => {
    const days = [];
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="text-center py-2" />);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        i
      );
      const isSelected =
        format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
      const isDisabled = minDate && date.getTime() < minDate.getTime();
      days.push(
        <div
          key={i}
          className={`w-10 h-10 flex items-center justify-center rounded-full text-sm ${
            isDisabled
              ? "text-gray-400 cursor-not-allowed"
              : isSelected
              ? "bg-cyan-600 text-white cursor-pointer"
              : "text-gray-700 hover:bg-cyan-100 cursor-pointer"
          }`}
          onClick={() => {
            if (!isDisabled) {
              onDateChange(date);
            }
          }}
        >
          {i}
        </div>
      );
    }
    return days;
  };

  return (
    <div
      className="bg-white shadow-[0px_2px_20px_0px_#0000001F] border-t border-[#E4E4E4] rounded-[10.56px] p-[15.09px] w-full h-full"
      style={{ boxSizing: "border-box" }}
    >
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth}>
          <span className="text-2xl">«</span>
        </button>
        <span className="font-medium">{format(currentMonth, "MMM yyyy")}</span>
        <button onClick={nextMonth}>
          <span className="text-2xl">»</span>
        </button>
      </div>

      <div className="grid grid-cols-7 text-xs text-gray-500 mb-2">
        {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
          <div key={d} className="text-center">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-1 text-center">{renderDays()}</div>
    </div>
  );
};

interface ScheduledCreative {
  id: string;
  title: string;
  imageUrl: string;
  orientation: "landscape" | "portrait";
  format: string;
  createdOn: string;
  dateRange: string;
  locations: string;
  tags: string[];
  isPlaying: boolean;
  scheduleType: "now" | "later" | "selection";
  announcement: "general" | "tag-level";
  scheduleDetails: any;
}

type FilterType =
  | "all"
  | "live"
  | "offline"
  | "scheduled"
  | "landscape"
  | "portrait";

const ScheduledCreativesListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setTitle, setShowBackButton, pushNavState } = useTopBar();

  const {
    scheduledCreatives,
    updateScheduledCreative,
    deleteScheduledCreative,
  } = useScheduledCreatives();

  // Add playlist context and creatives context
  const { playlists, getPlaylistWithImage } = usePlaylist();
  const { allCreatives } = useCreatives();

  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"creatives" | "playlists">(
    "creatives"
  );

  // Date filter states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);

  useEffect(() => {
    const title = "Scheduled";
    const showBack = false;
    setTitle(title);
    setShowBackButton(showBack);
    pushNavState({
      path: location.pathname,
      title,
      showBackButton: showBack,
    });
  }, [location.pathname, setTitle, setShowBackButton, pushNavState]);

  // Helper function to parse date range from creative.dateRange
  const parseDateRange = (
    dateRange: string
  ): { start: Date | null; end: Date | null } => {
    try {
      if (!dateRange || typeof dateRange !== "string") {
        return { start: null, end: null };
      }

      const normalizedRange = dateRange.trim();

      if (!normalizedRange.includes(" - ")) {
        try {
          const singleDate = new Date(normalizedRange);
          if (!isNaN(singleDate.getTime())) {
            return { start: singleDate, end: singleDate };
          }
        } catch (error) {
          console.warn("Failed to parse single date:", normalizedRange);
        }
        return { start: null, end: null };
      }

      const [startStr, endStr] = normalizedRange
        .split(" - ")
        .map((d) => d.trim());

      let startDate: Date | null = null;
      let endDate: Date | null = null;

      try {
        startDate = new Date(startStr);
        if (isNaN(startDate.getTime())) {
          startDate = null;
        }
      } catch (error) {
        console.warn("Failed to parse start date:", startStr);
      }

      try {
        endDate = new Date(endStr);
        if (isNaN(endDate.getTime())) {
          endDate = null;
        }
      } catch (error) {
        console.warn("Failed to parse end date:", endStr);
      }

      return { start: startDate, end: endDate };
    } catch (error) {
      console.warn("Error parsing date range:", dateRange, error);
      return { start: null, end: null };
    }
  };

  // Helper function to check if a date falls within a range
  const isDateInRange = (
    targetDate: Date,
    rangeStart: Date | null,
    rangeEnd: Date | null
  ): boolean => {
    if (!rangeStart && !rangeEnd) return false;

    const normalizeDate = (date: Date): Date => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };

    const normalizedTarget = normalizeDate(targetDate);

    if (rangeStart && rangeEnd && rangeStart.getTime() === rangeEnd.getTime()) {
      return normalizedTarget.getTime() === normalizeDate(rangeStart).getTime();
    }

    if (rangeStart && rangeEnd) {
      const normalizedStart = normalizeDate(rangeStart);
      const normalizedEnd = normalizeDate(rangeEnd);
      return (
        normalizedTarget >= normalizedStart && normalizedTarget <= normalizedEnd
      );
    }

    if (rangeStart && !rangeEnd) {
      return normalizedTarget >= normalizeDate(rangeStart);
    }

    if (!rangeStart && rangeEnd) {
      return normalizedTarget <= normalizeDate(rangeEnd);
    }

    return false;
  };

  const filteredCreatives = scheduledCreatives.filter((creative) => {
    const matchesSearch = creative.title
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchesFilter =
      filter === "all" ||
      (filter === "landscape" && creative.orientation === "landscape") ||
      (filter === "portrait" && creative.orientation === "portrait");

    let matchesDateRange = true;
    if (startDate || endDate) {
      const { start: creativeStartDate, end: creativeEndDate } = parseDateRange(
        creative.dateRange
      );

      if (startDate && endDate) {
        if (creativeStartDate && creativeEndDate) {
          const normalizeDate = (date: Date): Date => {
            const normalized = new Date(date);
            normalized.setHours(0, 0, 0, 0);
            return normalized;
          };

          const selectedStart = normalizeDate(startDate);
          const selectedEnd = normalizeDate(endDate);
          const creativeStart = normalizeDate(creativeStartDate);
          const creativeEnd = normalizeDate(creativeEndDate);

          matchesDateRange = !(
            creativeEnd < selectedStart || creativeStart > selectedEnd
          );
        } else {
          matchesDateRange = false;
        }
      } else if (startDate && !endDate) {
        matchesDateRange = isDateInRange(
          startDate,
          creativeStartDate,
          creativeEndDate
        );
      } else if (!startDate && endDate) {
        matchesDateRange = isDateInRange(
          endDate,
          creativeStartDate,
          creativeEndDate
        );
      }
    }

    return matchesSearch && matchesFilter && matchesDateRange;
  });

  // Get scheduled playlists (playlists that are playing or scheduled) - IMPROVED LOGIC
  const scheduledPlaylists = playlists.filter((playlist) => {
    // Check if playlist is scheduled/playing
    const isScheduled = playlist.status === "Playing" || playlist.isScheduled;
    
    // Check search match
    const matchesSearch = playlist.title?.toLowerCase().includes(search.toLowerCase());
    
    // Only return playlists that are actually scheduled AND match search
    return isScheduled && matchesSearch;
  });

  // Get playlists with images for display - IMPROVED WITH BETTER IMAGE HANDLING
  const scheduledPlaylistsWithImages = scheduledPlaylists.map(playlist => {
    // Get playlist with dynamic image based on its content
    const playlistWithImage = getPlaylistWithImage 
      ? getPlaylistWithImage(playlist.id, allCreatives || []) 
      : playlist;
    
    // Ensure we have a valid playlist object
    const finalPlaylist = playlistWithImage || playlist;
    
    // Add fallback values to ensure PlaylistCard works properly
    return {
      ...finalPlaylist,
      creativesCount: finalPlaylist.creativesCount || 0,
      imageUrl: finalPlaylist.imageUrl || '',
      status: finalPlaylist.status || 'Draft',
      isScheduled: finalPlaylist.isScheduled || false
    };
  });

  const handleCreativeUpdate = (updatedCreative: ScheduledCreative) => {
    updateScheduledCreative(updatedCreative.id, updatedCreative);
  };

  const handleCreativeDelete = (creativeId: string) => {
    deleteScheduledCreative(creativeId);
  };

  const handleAddNewCreative = () => {
    navigate("/scheduled/schedule-creative");
  };

  const handlePlaylistClick = (playlistId: string) => {
    navigate(`/playlists/playlist/${playlistId}`);
  };

  const handleStartDateChange = (date: Date) => {
    setStartDate(date);
    setShowStartCalendar(false);
  };

  const handleEndDateChange = (date: Date) => {
    setEndDate(date);
    setShowEndCalendar(false);
  };

  const clearStartDate = () => {
    setStartDate(null);
  };

  const clearEndDate = () => {
    setEndDate(null);
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header Section */}
      <div className="flex justify-between items-center px-6 py-4">
        {/* Toggle Buttons */}
        <div className="flex border-b w-fit">
          <button
            onClick={() => setActiveTab("creatives")}
            className={`px-4 py-2 text-lg 2xl:text-xl font-semibold ${
              activeTab === "creatives"
                ? "text-[#0084A4] border-b-[1.6px] border-[#0084A4]"
                : "text-[#808191] border-b-[1.6px] border-[#808191]"
            }`}
          >
            Creatives
          </button>
          <button
            onClick={() => setActiveTab("playlists")}
            className={`px-4 py-2 text-lg 2xl:text-xl font-semibold ${
              activeTab === "playlists"
                ? "text-[#0084A4] border-b-[1.6px] border-[#0084A4]"
                : "text-[#808191] border-b-[1.6px] border-[#808191]"
            }`}
          >
            Playlists ({scheduledPlaylistsWithImages.length})
          </button>
        </div>

        {/* Right: Search + Date Filters + Filters + Button */}
        <div className="flex items-center gap-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 3xl:w-6 3xl:h-6" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${activeTab}...`}
              className="w-64 pl-10 pr-4 3xl:pl-12 py-2.5 rounded-[8px] border border-gray-300 focus:outline-none placeholder:text-sm placeholder:font-medium"
            />
          </div>

          {/* Date filters - only show for creatives tab */}
          {activeTab === "creatives" && (
            <>
              {/* Start Date Filter */}
              <div className="relative 2xl:w-52">
                <div
                  className="flex items-center 2xl:justify-between gap-2 px-3 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setShowStartCalendar(!showStartCalendar);
                    setShowEndCalendar(false);
                  }}
                >
                  <span className="text-sm font-medium text-gray-700">
                    {startDate ? format(startDate, "MMM dd, yyyy") : "Start date"}
                  </span>
                  {startDate && (
                    <X
                      className="w-4 h-4 3xl:w-6 3xl:h-6 ml-[32px] text-gray-400 hover:text-gray-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearStartDate();
                      }}
                    />
                  )}
                  <Calendar className="w-4 h-4 3xl:w-6 3xl:h-6 text-gray-400" />
                </div>
                {showStartCalendar && (
                  <div className="absolute top-full left-0 mt-2 z-50 w-80">
                    <CustomCalendar
                      selectedDate={startDate || new Date()}
                      onDateChange={handleStartDateChange}
                    />
                  </div>
                )}
              </div>

              {/* End Date Filter */}
              <div className="relative 2xl:w-52">
                <div
                  className="flex items-center 2xl:justify-between gap-2 px-3 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setShowEndCalendar(!showEndCalendar);
                    setShowStartCalendar(false);
                  }}
                >
                  <span className="text-sm font-medium text-gray-700">
                    {endDate ? format(endDate, "MMM dd, yyyy") : "End date"}
                  </span>
                  {endDate && (
                    <X
                      className="w-4 h-4 3xl:w-6 3xl:h-6 text-gray-400 hover:text-gray-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearEndDate();
                      }}
                    />
                  )}
                  <Calendar className="w-4 h-4 3xl:w-6 3xl:h-6 text-gray-400" />
                </div>
                {showEndCalendar && (
                  <div className="absolute top-full left-0 mt-2 z-50 w-80">
                    <CustomCalendar
                      selectedDate={endDate || new Date()}
                      onDateChange={handleEndDateChange}
                      minDate={startDate || undefined}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Schedule New Button - ONLY show for creatives tab */}
          {activeTab === "creatives" && (
            <button
              onClick={handleAddNewCreative}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0091AF] text-white rounded-lg hover:bg-[#007A94] transition-colors duration-200 text-sm 3xl:text-base font-medium"
            >
              <Icon className="w-4 h-4 3xl:w-6 3xl:h-6" />
              Schedule New
            </button>
          )}

          {/* Orientation Filter Icons - only show for creatives tab */}
          {activeTab === "creatives" && (
            <div className="flex gap-1 text-gray-600">
              {[
                { key: "all", icon: <All className="w-5 h-5" /> },
                { key: "landscape", icon: <Landscape className="w-5 h-5" /> },
                { key: "portrait", icon: <Portrait className="w-5 h-5" /> },
              ].map(({ key, icon }) => (
                <div
                  key={key}
                  className={`p-2 rounded-md cursor-pointer ${
                    filter === key
                      ? "bg-[#F2FAFD] text-[#0090AD]"
                      : "hover:text-black"
                  }`}
                  onClick={() => setFilter(key as FilterType)}
                >
                  {icon}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1 p-6 overflow-y-auto">
        {activeTab === "creatives" ? (
          filteredCreatives.length === 0 ? (
            <div className="flex items-center justify-center mt-10">
              <div className="flex flex-col items-center text-center py-8">
                <div className="flex items-center justify-center mb-4">
                  <ScheduledCreative className="w-52 h-52" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 -mt-4 mb-2">
                  {scheduledCreatives.length === 0
                    ? "Scheduled creatives"
                    : "No creatives match your filters"}
                </h3>
                <p className="text-gray-500 mb-4">
                  {scheduledCreatives.length === 0
                    ? "View your scheduled creatives intended for display screens here."
                    : "Try adjusting your search, date range, or filters"}
                </p>
                {scheduledCreatives.length === 0 && (
                  <button
                    onClick={handleAddNewCreative}
                    className="flex items-center gap-2 px-6 py-3 bg-[#0091AF] text-white rounded-lg hover:bg-[#007A94] transition-colors duration-200 text-sm font-medium"
                  >
                    <Icon className="w-6 h-6" />
                    Schedule Creative
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
              {filteredCreatives.map((creative) => (
                <div key={creative.id} className="flex justify-center">
                  <ScheduledCreativesCard
                    creative={creative}
                    onUpdate={handleCreativeUpdate}
                    onDelete={handleCreativeDelete}
                    allCreatives={scheduledCreatives}
                  />
                </div>
              ))}
            </div>
          )
        ) : (
          /* Playlists Tab Content */
          scheduledPlaylistsWithImages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex items-center justify-center mb-4">
                <ScheduledCreative className="w-52 h-52" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {search 
                  ? "No scheduled playlists match your search"
                  : "No scheduled playlists"}
              </h3>
              <p className="text-gray-500 mb-4">
                {search
                  ? `No scheduled playlists match "${search}"`
                  : "Scheduled playlists will appear here when they are currently playing or scheduled to play."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 5xl:grid-cols-6 gap-6">
              {scheduledPlaylistsWithImages.map((playlist) => (
                <div
                  key={playlist.id}
                  onClick={() => handlePlaylistClick(playlist.id)}
                  className="cursor-pointer transform transition-transform hover:scale-105"
                >
                  <PlaylistCard
                    title={playlist.title}
                    creativesCount={playlist.creativesCount}
                    imageUrl={playlist.imageUrl}
                    status={playlist.status}
                    isScheduled={playlist.isScheduled}
                  />
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Overlay to close calendars when clicking outside */}
      {(showStartCalendar || showEndCalendar) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowStartCalendar(false);
            setShowEndCalendar(false);
          }}
        />
      )}
    </div>
  );
};

export default ScheduledCreativesListPage;